import { ValueMap, Session, APIResponse } from '../types';

// In development, use Vite proxy (/api -> localhost:8787)
// In production, VITE_API_URL should point to the Worker URL
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

class APIClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        credentials: 'include', // Include Cloudflare Access JWT
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }

      return data;
    } catch (error: any) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: error.message || 'Network error',
      };
    }
  }

  async parseSummary(executiveSummary: string, mode: 'simple' | 'advanced' = 'simple') {
    return this.request<{ sessionId: string; valueMap: ValueMap }>(
      '/api/parse-summary',
      {
        method: 'POST',
        body: JSON.stringify({ executiveSummary, mode }),
      }
    );
  }

  async refineValueMap(
    sessionId: string,
    message: string,
    currentValueMap: ValueMap
  ) {
    return this.request<{ response: string; updatedValueMap: ValueMap }>(
      '/api/refine',
      {
        method: 'POST',
        body: JSON.stringify({ sessionId, message, currentValueMap }),
      }
    );
  }

  async getSession(sessionId: string) {
    return this.request<Session>(`/api/sessions/${sessionId}`, {
      method: 'GET',
    });
  }

  async getSessions() {
    return this.request<{ sessions: Session[] }>('/api/sessions', {
      method: 'GET',
    });
  }

  async deleteSession(sessionId: string) {
    return this.request<{ deleted: boolean }>(`/api/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  async trackExport(sessionId: string, format: 'pdf' | 'png') {
    return this.request<{ tracked: boolean }>(`/api/export/${format}`, {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }
}

export const api = new APIClient();
